import { useMemo, useState } from 'react';
import { useModerationAudit } from '../useAdmin';
import type { ModerationAuditFilters, ModerationAuditRow } from '../adminApi';
import './AdminModerationAudit.css';

const PAGE_SIZE = 30;

const STAGE_OPTIONS = [
  { value: '', label: 'Any stage' },
  { value: 'csam_scan', label: 'CSAM scan' },
  { value: 'general_image', label: 'Image moderation' },
  { value: 'general_audio', label: 'Audio moderation' },
  { value: 'general_text', label: 'Text moderation' },
  { value: 'face_blur', label: 'Face blur' },
  { value: 'pipeline_summary', label: 'Pipeline summary' },
  { value: 'forward_to_human', label: 'Forward to human' },
];

const DECISION_OPTIONS = [
  { value: '', label: 'Any decision' },
  { value: 'ok', label: 'OK' },
  { value: 'block', label: 'Block' },
  { value: 'review', label: 'Review' },
];

/**
 * Admin moderation audit log viewer (F13). Read-only feed of every
 * moderation pipeline stage that ran on every submission.
 */
export function AdminModerationAudit(): JSX.Element {
  const [submissionId, setSubmissionId] = useState('');
  const [stage, setStage] = useState('');
  const [decision, setDecision] = useState('');
  const [page, setPage] = useState(0);

  const filters = useMemo<ModerationAuditFilters>(
    () => ({
      submission_id: submissionId.trim() || undefined,
      stage: stage || undefined,
      decision: decision || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [submissionId, stage, decision, page],
  );

  const { data, isLoading, isError, error, isFetching } = useModerationAudit(filters);

  const total = data?.total ?? 0;
  const showing = data?.rows.length ?? 0;
  const startIndex = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const endIndex = page * PAGE_SIZE + showing;
  const canPrev = page > 0;
  const canNext = (page + 1) * PAGE_SIZE < total;

  function reset<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(0);
    };
  }

  return (
    <section className="bl-admin-modaudit" aria-label="Moderation audit log">
      <header className="bl-admin-modaudit__header">
        <div>
          <h2>Moderation log</h2>
          <p className="bl-admin-modaudit__sub">
            Every stage the pipeline ran on every submission, newest first. Read-only — by design.
          </p>
        </div>
        <p className="bl-admin-modaudit__count" aria-live="polite">
          {isLoading
            ? 'Loading…'
            : total === 0
              ? 'No events match'
              : `${startIndex.toLocaleString()}–${endIndex.toLocaleString()} of ${total.toLocaleString()}`}
          {isFetching && !isLoading && ' · refreshing…'}
        </p>
      </header>

      <form
        className="bl-admin-modaudit__filters"
        onSubmit={(e) => e.preventDefault()}
        role="search"
      >
        <label className="bl-admin-modaudit__field">
          <span className="bl-admin-modaudit__field-label">Submission</span>
          <input
            type="text"
            value={submissionId}
            onChange={(e) => reset(setSubmissionId)(e.target.value)}
            placeholder="UUID"
            spellCheck={false}
          />
        </label>
        <label className="bl-admin-modaudit__field">
          <span className="bl-admin-modaudit__field-label">Stage</span>
          <select value={stage} onChange={(e) => reset(setStage)(e.target.value)}>
            {STAGE_OPTIONS.map((o) => (
              <option key={o.value || 'any'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="bl-admin-modaudit__field">
          <span className="bl-admin-modaudit__field-label">Decision</span>
          <select value={decision} onChange={(e) => reset(setDecision)(e.target.value)}>
            {DECISION_OPTIONS.map((o) => (
              <option key={o.value || 'any'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </form>

      {isError && (
        <p className="bl-admin-modaudit__error" role="alert">
          {error instanceof Error ? error.message : 'Could not load events.'}
        </p>
      )}

      <ul className="bl-admin-modaudit__list">
        {data?.rows.map((row) => (
          <Entry key={row.id} row={row} />
        ))}
        {!isLoading && data?.rows.length === 0 && (
          <li className="bl-admin-modaudit__empty">No events match the current filters.</li>
        )}
      </ul>

      <nav className="bl-admin-modaudit__pagination" aria-label="Pagination">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={!canPrev}
        >
          ← Previous
        </button>
        <span className="bl-admin-modaudit__page-label">
          Page {page + 1}
          {total > 0 && ` of ${Math.max(1, Math.ceil(total / PAGE_SIZE))}`}
        </span>
        <button type="button" onClick={() => setPage((p) => p + 1)} disabled={!canNext}>
          Next →
        </button>
      </nav>
    </section>
  );
}

function Entry({ row }: { row: ModerationAuditRow }): JSX.Element {
  const hasSignals = Object.keys(row.signals).length > 0;
  return (
    <li className="bl-admin-modaudit__entry" data-decision={row.decision}>
      <div className="bl-admin-modaudit__entry-head">
        <span className="bl-admin-modaudit__stage">{row.stage}</span>
        <span className="bl-admin-modaudit__provider">{row.provider}</span>
        <span
          className={`bl-admin-modaudit__decision bl-admin-modaudit__decision--${row.decision}`}
        >
          {row.decision}
        </span>
        <time className="bl-admin-modaudit__when" dateTime={row.created_at}>
          {formatDate(row.created_at)}
        </time>
      </div>
      <p className="bl-admin-modaudit__line">
        Submission <code className="bl-admin-modaudit__sid">{row.submission_id.slice(0, 8)}</code>
        {row.blob_id && (
          <>
            {' · blob '}
            <code className="bl-admin-modaudit__sid">{row.blob_id.slice(0, 8)}</code>
          </>
        )}
        {' · '}
        {row.reason}
      </p>
      {hasSignals && (
        <pre className="bl-admin-modaudit__signals">{JSON.stringify(row.signals, null, 2)}</pre>
      )}
    </li>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
